import { useCallback, useEffect, useState } from "react";

import {
  getVideoRentalAccess,
  createVideoRental,
  getMyVideoRentals,
} from "../services/videoRentalService";

const getMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Unable to process rental request.";

export const useVideoRental = (videoTag) => {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renting, setRenting] = useState(false);
  const [error, setError] = useState("");

  const checkAccess = useCallback(async () => {
    if (!videoTag) {
      setAccess(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError("");

    try {
      const response = await getVideoRentalAccess(videoTag);

      if (response?.success === false) {
        throw new Error(response.message || "Unable to check rental.");
      }

      const result = response?.access || response;

      setAccess(result);

      return result;
    } catch (requestError) {
      setAccess(null);
      setError(getMessage(requestError));
      return null;
    } finally {
      setLoading(false);
    }
  }, [videoTag]);

  const rentVideo = useCallback(async () => {
    if (!videoTag || renting) return false;

    setRenting(true);
    setError("");

    try {
      const response = await createVideoRental(videoTag);

      if (response?.success === false) {
        throw new Error(response.message || "Unable to start checkout.");
      }

      const checkoutUrl =
        response?.checkoutUrl || response?.url || response?.data?.checkoutUrl;

      if (!checkoutUrl) {
        throw new Error("Checkout URL is unavailable.");
      }

      const parsedUrl = new URL(checkoutUrl, window.location.origin);

      if (!["https:", "http:"].includes(parsedUrl.protocol)) {
        throw new Error("Invalid checkout URL.");
      }

      window.location.assign(parsedUrl.href);

      return true;
    } catch (requestError) {
      setError(getMessage(requestError));
      return false;
    } finally {
      setRenting(false);
    }
  }, [videoTag, renting]);

  useEffect(() => {
    let cancelled = false;

    if (!videoTag) {
      setAccess(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setAccess(null);

    getVideoRentalAccess(videoTag)
      .then((response) => {
        if (cancelled) return;

        if (response?.success === false) {
          throw new Error(response.message || "Unable to check rental.");
        }

        setAccess(response?.access || response);
      })
      .catch((requestError) => {
        if (cancelled) return;

        setError(getMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [videoTag]);

  return {
    access,
    loading,
    renting,
    error,
    checkAccess,
    rentVideo,
  };
};

export const useMyVideoRentals = () => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refreshRentals = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getMyVideoRentals();

      if (response?.success === false) {
        throw new Error(response.message || "Unable to load rentals.");
      }

      const items = Array.isArray(response)
        ? response
        : response?.rentals || response?.data?.rentals || [];

      setRentals(Array.isArray(items) ? items : []);

      return true;
    } catch (requestError) {
      setError(getMessage(requestError));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshRentals();
  }, [refreshRentals]);

  return {
    rentals,
    loading,
    error,
    refreshRentals,
  };
};
